import React, { useEffect, useMemo, useState } from "react";
import { Loader2, Search, ShieldOff, ShieldCheck, Users, Gift, CreditCard, Trash2 } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { TradesManager } from "@/components/TradesManager";
import { UnitsManager } from "@/components/UnitsManager";

const PLANS = ["free", "standard", "pro"];

export default function Admin() {
  const [tab, setTab] = useState("users");
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [refs, setRefs] = useState([]);
  const [log, setLog] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const removeUser = async (u) => {
    if (!window.confirm(`Remove ${u.company_name} (${u.email}) and all their data? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success(`${u.company_name} removed`);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not remove that account");
    }
  };

  const load = async () => {
    try {
      const [s, u, r, l] = await Promise.all([
        api.get("/admin/stats"), api.get("/admin/users"), api.get("/admin/referrals"),
        api.get("/admin/log"),
      ]);
      setStats(s.data); setUsers(u.data.users); setRefs(r.data.referrals); setLog(l.data.entries);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const rows = useMemo(() => {
    const n = q.trim().toLowerCase();
    return users.filter((u) => !n || `${u.company_name} ${u.email}`.toLowerCase().includes(n));
  }, [users, q]);

  const setPlan = async (u, plan) => {
    try {
      await api.put(`/admin/users/${u.id}/plan`, { plan, days: 31 });
      toast.success(`${u.company_name} → ${plan}`);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const toggleSuspend = async (u) => {
    try {
      await api.put(`/admin/users/${u.id}/suspend`, { suspended: !u.suspended });
      toast.success(u.suspended ? "Account reactivated" : "Account suspended and unlisted");
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const resetUsage = async (u) => {
    try {
      await api.put(`/admin/users/${u.id}/quota`, { bonus_quotes: u.bonus || 0, reset_usage: true });
      toast.success("Monthly usage reset");
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  if (loading) return <div className="flex justify-center py-32"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  const Stat = ({ icon: Icon, label, value }) => (
    <div className="bg-card border border-border rounded-2xl p-5">
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground font-semibold">
        <Icon className="w-4 h-4" /> {label}
      </div>
      <div className="font-display text-2xl font-extrabold mt-2">{value}</div>
    </div>
  );

  return (
    <div>
      <p className="text-xs uppercase tracking-widest text-brand font-bold mb-1">Admin</p>
      <h1 className="font-display text-3xl font-extrabold text-slate-900 dark:text-slate-100">Control panel</h1>
      <p className="text-muted-foreground text-sm mt-1 mb-8">Manage vendors, override subscriptions and track referrals.</p>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Stat icon={Users} label="Vendors" value={stats?.users ?? 0} />
        <Stat icon={CreditCard} label={`MRR (${stats?.currency})`} value={stats?.mrr ?? 0} />
        <Stat icon={Gift} label="Referrals" value={`${stats?.referrals_rewarded ?? 0} / ${stats?.referrals ?? 0}`} />
        <Stat icon={ShieldCheck} label="Paid payments" value={stats?.paid_transactions ?? 0} />
      </div>

      <div className="flex items-center gap-2 mb-4">
        {["users", "trades", "units", "referrals", "change log"].map((t) => (
          <button
            key={t}
            data-testid={`admin-tab-${t}`}
            onClick={() => setTab(t)}
            className={`px-4 py-2 rounded-lg text-sm font-semibold capitalize transition-colors ${tab === t ? "bg-primary text-primary-foreground" : "bg-card border border-border hover:border-primary/40"}`}
          >
            {t}
          </button>
        ))}
        {tab === "users" && (
          <div className="relative ml-auto">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input data-testid="admin-user-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search vendor or email" className="pl-9 h-10 w-64" />
          </div>
        )}
      </div>

      {tab === "trades" ? <TradesManager /> : tab === "units" ? <UnitsManager /> : (
      <div className="bg-card border border-border rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          {tab === "users" ? (
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 dark:bg-slate-900/60 border-b border-border text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="text-left font-semibold px-4 py-3 min-w-[200px]">Account</th>
                  <th className="text-left font-semibold px-3 py-3">Type</th>
                  <th className="text-left font-semibold px-3 py-3">Plan</th>
                  <th className="text-right font-semibold px-3 py-3">Used</th>
                  <th className="text-center font-semibold px-3 py-3">Items</th>
                  <th className="text-center font-semibold px-3 py-3">RFQs</th>
                  <th className="text-center font-semibold px-3 py-3">Verified</th>
                  <th className="text-left font-semibold px-3 py-3 w-60">Actions</th>
                </tr>
              </thead>
              <tbody data-testid="admin-users-body">
                {rows.map((u, i) => (
                  <tr key={u.id} data-testid={`admin-user-row-${i}`} className="border-b border-border last:border-0">
                    <td className="px-4 py-3">
                      <div className="font-semibold text-slate-900 dark:text-slate-100">
                        {u.company_name}{u.is_admin && <span className="ml-2 text-[10px] uppercase font-bold text-brand">admin</span>}
                      </div>
                      <div className="text-xs text-muted-foreground">{u.email}</div>
                    </td>
                    <td className="px-3 py-3">
                      <span data-testid={`admin-type-${i}`} className={`px-2 py-0.5 rounded-md text-[11px] font-bold uppercase ${u.account_type === "contractor" ? "bg-brand-soft text-brand" : "bg-primary/10 text-primary"}`}>
                        {u.account_type === "contractor" ? "Contractor" : u.account_type === "einvoice" ? "E-invoicing" : "Vendor"}
                      </span>
                    </td>
                    <td className="px-3 py-3">
                      <Select value={u.plan} onValueChange={(v) => setPlan(u, v)}>
                        <SelectTrigger data-testid={`admin-plan-${i}`} className="h-8 w-28 text-xs font-semibold bg-card"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-card">
                          {(u.account_type === "contractor" ? ["free", "pro"] : PLANS).map((p) => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </td>
                    <td data-testid={`admin-usage-${i}`} className="px-3 py-3 text-right font-mono text-xs">
                      {u.used} / {u.unlimited ? "∞" : u.quota + u.bonus}
                    </td>
                    <td className="px-3 py-3 text-center font-mono text-xs">{u.account_type === "contractor" ? "—" : u.catalogue_count}</td>
                    <td className="px-3 py-3 text-center font-mono text-xs">{u.rfq_count}</td>
                    <td className="px-3 py-3 text-center text-xs">{u.email_verified ? "Yes" : "No"}</td>
                    <td className="px-3 py-3">
                      <div className="flex items-center gap-1.5">
                        <Button size="sm" variant="outline" data-testid={`admin-reset-${i}`} onClick={() => resetUsage(u)} className="h-8 text-xs">Reset</Button>
                        <Button
                          size="sm"
                          variant={u.suspended ? "default" : "outline"}
                          data-testid={`admin-suspend-${i}`}
                          onClick={() => toggleSuspend(u)}
                          className="h-8 text-xs gap-1"
                        >
                          {u.suspended ? <><ShieldCheck className="w-3 h-3" /> Restore</> : <><ShieldOff className="w-3 h-3" /> Suspend</>}
                        </Button>
                        {!u.is_admin && (
                          <button
                            data-testid={`admin-delete-${i}`}
                            onClick={() => removeUser(u)}
                            title="Remove user and all their data"
                            className="w-8 h-8 rounded-md border border-border flex items-center justify-center text-muted-foreground hover:text-destructive hover:border-destructive/40 transition-colors"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : tab === "change log" ? (
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 dark:bg-slate-900/60 border-b border-border text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="text-left font-semibold px-4 py-3">When</th>
                  <th className="text-left font-semibold px-4 py-3">Admin</th>
                  <th className="text-left font-semibold px-4 py-3">Action</th>
                  <th className="text-left font-semibold px-4 py-3">Account</th>
                  <th className="text-left font-semibold px-4 py-3">Detail</th>
                </tr>
              </thead>
              <tbody data-testid="admin-log-body">
                {log.length === 0 ? (
                  <tr><td colSpan={5} className="py-16 text-center text-muted-foreground">No admin changes recorded yet.</td></tr>
                ) : log.map((x, i) => (
                  <tr key={x.id} data-testid={`admin-log-row-${i}`} className="border-b border-border last:border-0">
                    <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">
                      {new Date(x.at).toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-xs">{x.admin_email}</td>
                    <td className="px-4 py-3 font-semibold capitalize">{x.action}</td>
                    <td className="px-4 py-3">
                      <div className="font-semibold">{x.target_company || "—"}</div>
                      <div className="text-xs text-muted-foreground">{x.target_email}</div>
                    </td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">{x.detail || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 dark:bg-slate-900/60 border-b border-border text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="text-left font-semibold px-4 py-3">Referrer</th>
                  <th className="text-left font-semibold px-4 py-3">Code</th>
                  <th className="text-left font-semibold px-4 py-3">Referred vendor</th>
                  <th className="text-left font-semibold px-4 py-3">Status</th>
                  <th className="text-left font-semibold px-4 py-3">Reward</th>
                </tr>
              </thead>
              <tbody data-testid="admin-referrals-body">
                {refs.length === 0 ? (
                  <tr><td colSpan={5} className="py-16 text-center text-muted-foreground">No referrals yet.</td></tr>
                ) : refs.map((x, i) => (
                  <tr key={x.id} data-testid={`admin-referral-row-${i}`} className="border-b border-border last:border-0">
                    <td className="px-4 py-3">
                      <div className="font-semibold">{x.referrer_company}</div>
                      <div className="text-xs text-muted-foreground">{x.referrer_email}</div>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs">{x.code}</td>
                    <td className="px-4 py-3">
                      <div className="font-semibold">{x.referred_company}</div>
                      <div className="text-xs text-muted-foreground">{x.referred_email}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-md text-[11px] font-bold ${x.status === "rewarded" ? "bg-brand-soft text-brand" : "bg-secondary text-muted-foreground"}`}>
                        {x.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">{x.reward || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
      )}
    </div>
  );
}
